import { useState, useEffect, useRef, useCallback, type CSSProperties } from "react";
import {
  ChatMessage,
  ChatInput,
  ChatOptionList,
  HeroRecommendationCard,
  ComparisonSlideout,
  ShimmerCard,
  MiniShimmerCard,
  ProductDetailsSlideout,
  Button,
  QuestionWizard,
} from "./components";
import type { ComparisonProduct } from "./components";
import { CompareIcon, EyeIcon } from "./components/Button/icons";
import { DEFAULT_HEADER_ACTIONS, useHeaderActions } from "./components/Layout/HeaderActionsContext";
import { chatService, type ApiChatMessage } from "./services/chatService";
import { TalkToSalesModal, type TalkToSalesFormData } from "./components/TalkToSalesModal/TalkToSalesModal";
import { leadService } from "./services/leadService";
import type {
  ChatState,
  ProductRecommendation,
  ChatOption,
  QuizState,
  UIMessage,
} from "./types/chat";
import {
  transformBackendResponseToUIMessage,
  transformOptionsToUIOptions,
  transformProductToRecommendation,
  createUserMessage,
  createLoadingMessage,
  getCurrentTimeString,
  createInitialQuizState,
  createQuizQuestionMessage,
  addQuizAnswer,
  getCurrentQuizOptions,
  createAnswerSummaryMessage,
  QUIZ_QUESTIONS,
} from "./utils/chatUtils";
import { shareProduct } from "./utils/shareUtils";
import { analyticsService } from './services/analyticsService';
import "./App.css";

type DrawerSnap = "collapsed" | "expanded";

const MOBILE_DRAWER_COLLAPSED_HEIGHT = 120;
const MOBILE_DRAWER_TOP_GAP = 84;
const MOBILE_DRAWER_EXPANDED_RATIO = 0.85;
const MOBILE_DRAWER_DRAG_THRESHOLD = 6;
const TABLET_BREAKPOINT = 768;

interface DrawerSnapHeights {
  collapsed: number;
  expanded: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

const getSnapHeights = (viewportHeight: number): DrawerSnapHeights => {
  const collapsed = MOBILE_DRAWER_COLLAPSED_HEIGHT;
  const expandedCap = Math.max(collapsed, Math.floor(viewportHeight - MOBILE_DRAWER_TOP_GAP));
  const expanded = clamp(
    Math.floor(viewportHeight * MOBILE_DRAWER_EXPANDED_RATIO),
    collapsed,
    expandedCap
  );

  return { collapsed, expanded };
};

const getNearestSnap = (
  height: number,
  snapHeights: DrawerSnapHeights
): DrawerSnap => {
  const candidates: Array<{ snap: DrawerSnap; delta: number }> = [
    { snap: "collapsed", delta: Math.abs(height - snapHeights.collapsed) },
    { snap: "expanded", delta: Math.abs(height - snapHeights.expanded) },
  ];

  candidates.sort((a, b) => a.delta - b.delta);
  return candidates[0].snap;
};

const isTextInputElement = (element: Element | null): boolean => {
  if (!element) return false;
  if (element instanceof HTMLTextAreaElement) return !element.disabled && !element.readOnly;
  if (element instanceof HTMLInputElement) {
    const nonTextTypes = new Set([
      "button",
      "checkbox",
      "color",
      "file",
      "hidden",
      "image",
      "radio",
      "range",
      "reset",
      "submit",
    ]);

    return !nonTextTypes.has(element.type.toLowerCase()) && !element.disabled && !element.readOnly;
  }

  return element instanceof HTMLElement && element.isContentEditable;
};

const createInitialChatStateValue = (): ChatState => ({
  messages: [],
  currentOptions: [],
  isLoading: false,
  topRecommendation: null,
  additionalRecommendations: [],
  quizState: createInitialQuizState(),
});

function App() {
  const [chatState, setChatState] = useState<ChatState>(createInitialChatStateValue);

  // Wizard state
  const [showWizard, setShowWizard] = useState(true);
  const [wizardAnswers, setWizardAnswers] = useState<string[]>([]);

  // Comparison state
  const [comparedProducts, setComparedProducts] = useState<ComparisonProduct[]>(
    []
  );
  const [isComparisonSlideoutOpen, setIsComparisonSlideoutOpen] =
    useState(false);

  // Mini card details state
  const [selectedMiniCardDetails, setSelectedMiniCardDetails] =
    useState<ProductRecommendation | null>(null);
  const [isMiniCardDetailsOpen, setIsMiniCardDetailsOpen] = useState(false);

  // Contact Sales modal state
  const [isContactSalesModalOpen, setIsContactSalesModalOpen] = useState(false);

  // Chat panel/drawer responsive state
  const initialViewportHeight =
    typeof window !== "undefined"
      ? window.innerHeight
      : 844;
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth < TABLET_BREAKPOINT : false
  );
  const [isDesktopChatOpen, setIsDesktopChatOpen] = useState(true);
  const [hasInteractedWithChat, setHasInteractedWithChat] = useState(false);
  const [lastSeenMobileMessageCount, setLastSeenMobileMessageCount] = useState(0);
  const [snapHeights, setSnapHeights] = useState<DrawerSnapHeights>(
    getSnapHeights(initialViewportHeight)
  );
  const [drawerSnap, setDrawerSnap] = useState<DrawerSnap>("collapsed");
  const [drawerHeightPx, setDrawerHeightPx] = useState(
    getSnapHeights(initialViewportHeight).collapsed
  );
  const [isDrawerDragging, setIsDrawerDragging] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const { setHeaderActions } = useHeaderActions();
  const drawerHeightRef = useRef(drawerHeightPx);
  const drawerSnapRef = useRef(drawerSnap);
  const dragSessionRef = useRef<{
    pointerId: number;
    startY: number;
    startHeight: number;
    didDrag: boolean;
  } | null>(null);

  // Refs for desktop/mobile chat scroll containers
  const desktopChatMessagesRef = useRef<HTMLDivElement>(null);
  const mobileChatMessagesRef = useRef<HTMLDivElement>(null);
  const programmaticScrollTimeoutsRef = useRef<Map<HTMLDivElement, number>>(new Map());

  // Convert UI messages to API format for backend communication
  const convertMessagesToApiFormat = (messages: UIMessage[]): ApiChatMessage[] => {
    const apiMessages: ApiChatMessage[] = [];
    
    for (const message of messages) {
      // Skip messages that don't have export_as defined
      if (!message.export_as) {
        continue;
      }
      
      let role: "user" | "assistant";
      let content = message.content;
      
      // Use the export_as field to determine the role
      switch (message.export_as) {
        case 'assistant':
          role = 'assistant';
          // Bundle options into the content if they exist
          if (message.options && message.options.length > 0) {
            content += '\n\nOptions: ' + message.options.join(', ');
          }
          break;
        case 'user':
          role = 'user';
          break;
        default:
          // Skip if export_as is unknown (shouldn't happen with TS)
          continue;
      }
      
      apiMessages.push({ role, content });
    }
    
    return apiMessages;
  };

  // Helper functions to reduce repetitive state updates
  const addMessages = (newMessages: UIMessage[]) => {
    setChatState(prev => ({
      ...prev,
      messages: [...prev.messages, ...newMessages]
    }));
  };

  const replaceLastMessage = (newMessage: UIMessage) => {
    setChatState(prev => ({
      ...prev,
      messages: [...prev.messages.slice(0, -1), newMessage]
    }));
  };

  const setLoadingState = (isLoading: boolean) => {
    setChatState(prev => ({
      ...prev,
      isLoading,
      currentOptions: isLoading ? [] : prev.currentOptions
    }));
  };

  const updateRecommendations = (response: any, options: ChatOption[] = []) => {
    setChatState(prev => ({
      ...prev,
      currentOptions: options,
      isLoading: false,
      topRecommendation: transformProductToRecommendation(response.top_product, true),
      additionalRecommendations: response.you_might_also_like
        .map((product: any) => transformProductToRecommendation(product, false))
        .filter((rec: any): rec is ProductRecommendation => rec !== null)
    }));
  };

  const updateRecommendationsIfAvailable = (response: any, options: ChatOption[] = []) => {
    // Check if response contains products
    const hasProducts = response.top_product || (response.you_might_also_like && response.you_might_also_like.length > 0);
    
    if (hasProducts) {
      // Update recommendations with new products
      updateRecommendations(response, options);
    } else {
      // No products in response - just update loading state and options, keep existing recommendations
      setChatState(prev => ({
        ...prev,
        currentOptions: options,
        isLoading: false
      }));
    }
  };

  const callBackendAndUpdateState = async (
    message: string, 
    showUserMessage = true
  ) => {
    try {
      // Add user message if needed
      if (showUserMessage) {
        addMessages([createUserMessage(message)]);
      }
      
      // Show loading
      addMessages([createLoadingMessage()]);
      setLoadingState(true);

      // Call backend
      const response = await chatService.sendMessage(
        message,
        convertMessagesToApiFormat(chatState.messages),
        "gpt-4.1",
        chatState.topRecommendation as Record<string, unknown> | null,
        chatState.additionalRecommendations as unknown as Record<string, unknown>[]
      );

      // Update with response
      const assistantMessage = transformBackendResponseToUIMessage(response, getCurrentTimeString());
      replaceLastMessage(assistantMessage);
      updateRecommendationsIfAvailable(response, response.options ? transformOptionsToUIOptions(response.options) : []);

      return response;
    } catch (error) {
      console.error("Error in backend call:", error);
      const errorMessage = {
        type: "assistant" as const,
        content: "Sorry, I encountered an error while processing your request. Please try again.",
        author: "Solution assistant",
        timestamp: getCurrentTimeString(),
      };
      replaceLastMessage(errorMessage);
      setLoadingState(false);
      throw error;
    }
  };

  // Initialize chat with welcome message and health check
  useEffect(() => {
    if (!showWizard) {
      initializeChat();
    }
  }, [showWizard, wizardAnswers]);

  // Auto-scroll to bottom when new messages are added
  const scrollToBottom = () => {
    const markProgrammaticScroll = (container: HTMLDivElement | null) => {
      if (!container) return;

      container.dataset.programmaticScroll = "true";
      const existingTimeout = programmaticScrollTimeoutsRef.current.get(container);
      if (existingTimeout !== undefined) {
        window.clearTimeout(existingTimeout);
      }

      const timeoutId = window.setTimeout(() => {
        if (container.dataset.programmaticScroll === "true") {
          delete container.dataset.programmaticScroll;
        }
        programmaticScrollTimeoutsRef.current.delete(container);
      }, 180);

      programmaticScrollTimeoutsRef.current.set(container, timeoutId);
    };

    if (desktopChatMessagesRef.current) {
      markProgrammaticScroll(desktopChatMessagesRef.current);
      desktopChatMessagesRef.current.scrollTop = desktopChatMessagesRef.current.scrollHeight;
    }
    if (mobileChatMessagesRef.current) {
      markProgrammaticScroll(mobileChatMessagesRef.current);
      mobileChatMessagesRef.current.scrollTop = mobileChatMessagesRef.current.scrollHeight;
    }
  };

  // Trigger scroll whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [chatState.messages]);

  useEffect(() => {
    const containers = [desktopChatMessagesRef.current, mobileChatMessagesRef.current].filter(
      (container): container is HTMLDivElement => container !== null
    );

    if (containers.length === 0) return;

    const scrollTimeouts = new Map<HTMLDivElement, number>();

    const handleScroll = (event: Event) => {
      const container = event.currentTarget;
      if (!(container instanceof HTMLDivElement)) return;
      if (container.dataset.programmaticScroll === "true") return;

      container.classList.add("is-scrolling");
      const existingTimeout = scrollTimeouts.get(container);
      if (existingTimeout !== undefined) {
        window.clearTimeout(existingTimeout);
      }

      const timeoutId = window.setTimeout(() => {
        container.classList.remove("is-scrolling");
        scrollTimeouts.delete(container);
      }, 700);

      scrollTimeouts.set(container, timeoutId);
    };

    for (const container of containers) {
      container.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      for (const container of containers) {
        container.removeEventListener("scroll", handleScroll);
        container.classList.remove("is-scrolling");
        const timeoutId = scrollTimeouts.get(container);
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId);
        }
        const programmaticTimeout = programmaticScrollTimeoutsRef.current.get(container);
        if (programmaticTimeout !== undefined) {
          window.clearTimeout(programmaticTimeout);
          programmaticScrollTimeoutsRef.current.delete(container);
        }
        delete container.dataset.programmaticScroll;
      }
      scrollTimeouts.clear();
    };
  }, [isMobileViewport, hasInteractedWithChat, chatState.messages.length > 0]);

  useEffect(() => {
    if (chatState.messages.length > 0) {
      setHasInteractedWithChat(true);
    }
  }, [chatState.messages.length]);

  useEffect(() => {
    const mobileDrawerVisible = isMobileViewport && (hasInteractedWithChat || chatState.messages.length > 0);
    const isCollapsed = drawerHeightPx <= snapHeights.collapsed + 8;

    if (!mobileDrawerVisible || !isCollapsed) {
      setLastSeenMobileMessageCount(chatState.messages.length);
    }
  }, [
    isMobileViewport,
    hasInteractedWithChat,
    chatState.messages.length,
    drawerHeightPx,
    snapHeights.collapsed,
  ]);

  const snapTo = useCallback(
    (snap: DrawerSnap) => {
      setDrawerSnap(snap);
      const nextHeight = snapHeights[snap];
      setDrawerHeightPx(nextHeight);
    },
    [snapHeights]
  );

  const cycleDrawerSnap = useCallback(() => {
    const next: DrawerSnap = drawerSnap === "collapsed" ? "expanded" : "collapsed";
    snapTo(next);
  }, [drawerSnap, snapTo]);

  useEffect(() => {
    drawerHeightRef.current = drawerHeightPx;
  }, [drawerHeightPx]);

  useEffect(() => {
    drawerSnapRef.current = drawerSnap;
  }, [drawerSnap]);

  useEffect(() => {
    const handleViewportResize = () => {
      const viewportHeight = window.innerHeight;
      const nextSnapHeights = getSnapHeights(viewportHeight);
      setSnapHeights(nextSnapHeights);
      setIsMobileViewport(window.innerWidth < TABLET_BREAKPOINT);
      setDrawerHeightPx((prev) =>
        drawerSnapRef.current === "expanded"
          ? nextSnapHeights.expanded
          : clamp(prev, nextSnapHeights.collapsed, nextSnapHeights.expanded)
      );
    };

    handleViewportResize();

    window.addEventListener("resize", handleViewportResize, { passive: true });
    window.visualViewport?.addEventListener("resize", handleViewportResize, { passive: true });

    return () => {
      window.removeEventListener("resize", handleViewportResize);
      window.visualViewport?.removeEventListener("resize", handleViewportResize);
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setKeyboardHeight(0);
      setKeyboardOpen(false);
      setDrawerSnap("collapsed");
      setDrawerHeightPx(snapHeights.collapsed);
      return;
    }

    let rafId: number | null = null;
    const handleKeyboardViewport = () => {
      const viewport = window.visualViewport;
      if (!viewport) return;

      const layoutHeight = document.documentElement.clientHeight;
      const offset = Math.max(0, layoutHeight - viewport.height - viewport.offsetTop);
      const resolvedOffset = offset >= 40 ? offset : 0;
      const isKeyboardVisible =
        isTextInputElement(document.activeElement) && resolvedOffset > 0;

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        setKeyboardHeight(resolvedOffset);
        setKeyboardOpen(isKeyboardVisible);
      });
    };

    handleKeyboardViewport();
    window.visualViewport?.addEventListener("resize", handleKeyboardViewport, { passive: true });
    window.visualViewport?.addEventListener("scroll", handleKeyboardViewport, { passive: true });
    window.addEventListener("focusin", handleKeyboardViewport);
    window.addEventListener("focusout", handleKeyboardViewport);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      window.visualViewport?.removeEventListener("resize", handleKeyboardViewport);
      window.visualViewport?.removeEventListener("scroll", handleKeyboardViewport);
      window.removeEventListener("focusin", handleKeyboardViewport);
      window.removeEventListener("focusout", handleKeyboardViewport);
    };
  }, [isMobileViewport, snapHeights.collapsed]);

  const handleDrawerPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragSessionRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: drawerHeightRef.current,
      didDrag: false,
    };
  };

  const handleDrawerPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const session = dragSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;

    const deltaY = session.startY - event.clientY;
    if (!session.didDrag && Math.abs(deltaY) < MOBILE_DRAWER_DRAG_THRESHOLD) return;

    if (!session.didDrag) {
      session.didDrag = true;
      setIsDrawerDragging(true);
    }

    const nextHeight = clamp(
      session.startHeight + deltaY,
      snapHeights.collapsed,
      snapHeights.expanded
    );
    setDrawerHeightPx(nextHeight);
  };

  const finalizeDrawerDrag = (didDrag: boolean) => {
    setIsDrawerDragging(false);
    if (didDrag) {
      snapTo(getNearestSnap(drawerHeightRef.current, snapHeights));
      return;
    }
    cycleDrawerSnap();
  };

  const handleDrawerPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    const session = dragSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    dragSessionRef.current = null;
    finalizeDrawerDrag(session.didDrag);
  };

  const handleDrawerPointerCancel = () => {
    const session = dragSessionRef.current;
    dragSessionRef.current = null;
    if (session?.didDrag) {
      finalizeDrawerDrag(true);
    }
  };

  const initializeChat = async () => {
    try {
      // Check server health
      await chatService.checkHealth();
      console.log("Server health check passed");
    } catch (error) {
      console.error("Health check failed:", error);
    }

    // Add welcome message and summary
    const welcomeMessage =
      "Hi! 👋 Based on your answers, here are the perfect AV solutions for your space.";

    // Create summary of wizard answers
    const answerSummary = wizardAnswers.length > 0 
      ? `Based on your selections:\n• ${wizardAnswers.join('\n• ')}`
      : '';

    const summaryMessage = {
      type: "assistant" as const,
      content: answerSummary,
      author: "Solution assistant",
      timestamp: getCurrentTimeString(),
      export_as: 'assistant' as const,
    };

    // Set up the chat state with completed quiz
    setChatState((prev) => ({
      ...prev,
      messages: [
        {
          type: "assistant",
          content: welcomeMessage,
          author: "Solution assistant",
          timestamp: getCurrentTimeString(),
        },
        summaryMessage
      ],
      currentOptions: [],
      quizState: {
        ...prev.quizState,
        isActive: false,
        isComplete: true,
        currentQuestionNumber: 4, // Beyond the last question
        answers: wizardAnswers.map((answer, index) => ({
          questionNumber: index + 1,
          question: QUIZ_QUESTIONS[index].question,
          answer
        }))
      }
    }));

    // Immediately get recommendations based on wizard answers
    await getInitialRecommendations();
  };

  // Helper function to check if message is a valid quiz answer
  const isValidQuizAnswer = (message: string): boolean => {
    if (!chatState.quizState.isActive) return false;

    const currentOptions = getCurrentQuizOptions(
      chatState.quizState.currentQuestionNumber
    );
    return currentOptions.some((option) => option.value === message);
  };

  // Event handlers - simplified with helper functions
  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;
    setHasInteractedWithChat(true);

    if (isMobileViewport && drawerSnap === "collapsed") {
      snapTo("expanded");
    }

    // If quiz is active and this is a valid quiz answer, handle quiz flow
    if (chatState.quizState.isActive && isValidQuizAnswer(message)) {
      handleQuizAnswer(message);
      return;
    }

    // Regular chat flow (after quiz is complete)
    await callBackendAndUpdateState(message);
  };

  const handleQuizAnswer = async (answer: string) => {
    // Update quiz state with the answer
    const updatedQuizState = addQuizAnswer(
      chatState.quizState,
      chatState.quizState.currentQuestionNumber,
      answer
    );

    const { answers } = updatedQuizState;
    const answerCount = answers.length;
    const isComplete = updatedQuizState.isComplete;
    const justCompletedRequired = answerCount === 3;

    // Update quiz state
    setChatState(prev => ({ ...prev, quizState: updatedQuizState }));

    if (isComplete) {
      // Final question (5) - show user message and complete quiz
      addMessages([createUserMessage(answer)]);
      await handleQuizComplete(updatedQuizState, true, answer);
    } else if (justCompletedRequired) {
      // Question 3 - show user message, summary, and get initial recommendations
      const summaryMessage = createAnswerSummaryMessage(answers);
      addMessages([createUserMessage(answer), summaryMessage]);
      await handleQuizComplete(updatedQuizState, false, answer);
    } else if (answerCount <= 2) {
      // Questions 1-2 - show user message and next question
      const nextQuestion = createQuizQuestionMessage(updatedQuizState.currentQuestionNumber);
      const nextOptions = getCurrentQuizOptions(updatedQuizState.currentQuestionNumber);
      
      addMessages([createUserMessage(answer), nextQuestion]);
      setChatState(prev => ({ ...prev, currentOptions: nextOptions }));
    } else {
      // Question 4 - show user message, get updated recommendations, then next question
      addMessages([createUserMessage(answer)]);
      await handleQuizComplete(updatedQuizState, false, answer);
    }
  };

  const handleQuizComplete = async (
    completedQuizState: QuizState,
    endQuiz: boolean = true,
    userAnswer?: string
  ) => {
    try {
      // Show loading
      addMessages([createLoadingMessage()]);
      setLoadingState(true);

      // Use the provided answer or get the most recent user message
      const message = userAnswer || (() => {
        const userMessages = chatState.messages.filter(msg => msg.type === 'user-response');
        return userMessages.length > 0 ? userMessages[userMessages.length - 1].content : '';
      })();
      
      const response = await chatService.sendMessage(
        message,
        convertMessagesToApiFormat(chatState.messages),
        "gpt-4.1",
        chatState.topRecommendation as Record<string, unknown> | null,
        chatState.additionalRecommendations as unknown as Record<string, unknown>[]
      );

      // Remove loading message and add assistant response
      const assistantMessage = transformBackendResponseToUIMessage(response, getCurrentTimeString());

      if (endQuiz) {
        // Final completion - end quiz and enable chat
        replaceLastMessage(assistantMessage);
        updateRecommendationsIfAvailable(response, response.options ? transformOptionsToUIOptions(response.options) : []);
      } else {
        // Show recommendations but continue with next question (if available)
        const hasNextQuestion =
          completedQuizState.currentQuestionNumber <= QUIZ_QUESTIONS.length;

        replaceLastMessage(assistantMessage);
        updateRecommendationsIfAvailable(response);

        if (hasNextQuestion) {
          const nextQuestion = createQuizQuestionMessage(
            completedQuizState.currentQuestionNumber
          );
          const nextOptions = getCurrentQuizOptions(
            completedQuizState.currentQuestionNumber
          );
          
          addMessages([nextQuestion]);
          setChatState(prev => ({ ...prev, currentOptions: nextOptions }));
        } else {
          // No more questions - end quiz and enable chat
          setChatState(prev => ({ 
            ...prev, 
            currentOptions: response.options ? transformOptionsToUIOptions(response.options) : [],
            quizState: { ...prev.quizState, isActive: false, isComplete: true }
          }));
        }
      }
    } catch (error) {
      console.error("Error completing quiz:", error);

      // Show error message
      const errorMessage = {
        type: "assistant" as const,
        content: "Sorry, I encountered an error while processing your answers. Please try again.",
        author: "Solution assistant",
        timestamp: getCurrentTimeString(),
      };
      
      replaceLastMessage(errorMessage);
      setLoadingState(false);
    }
  };

  const handleOptionSelect = (option: ChatOption) => {
    setHasInteractedWithChat(true);
    if (isMobileViewport && drawerSnap === "collapsed") {
      snapTo("expanded");
    }
    // Send the selected option as a message (works for both quiz and chat)
    handleSendMessage(option.value);
  };

    const handleViewDetails = () => {
        if (chatState.topRecommendation) {
            analyticsService.trackEvent('view_product', {
                product_name: chatState.topRecommendation.productName,
                product_sku: chatState.topRecommendation.model,
                source: 'hero_card',
            });
        }
        // TODO: Implement product details modal/page
    };

    const handleMiniCardViewDetails = (recommendation: ProductRecommendation) => {
        analyticsService.trackEvent('view_product', {
            product_name: recommendation.productName,
            product_sku: recommendation.model,
            source: 'mini_card',
        });
        
        setSelectedMiniCardDetails(recommendation);
        setIsMiniCardDetailsOpen(true);
    };

    const handleCloseMiniCardDetails = () => {
        setIsMiniCardDetailsOpen(false);
        setSelectedMiniCardDetails(null);
    };

    // Contact Sales handlers
    const handleContactSales = useCallback(() => {
        setIsContactSalesModalOpen(true);
    }, []);

    const handleContactSalesSubmit = async (formData: TalkToSalesFormData) => {
        try {
            console.log('Submitting Contact Sales form:', formData);
            
            // Create lead using the lead service
            const lead = await leadService.createLead(formData);
            
            console.log('Lead created successfully:', lead);
        } catch (error) {
            console.error('Failed to create lead:', error);
            
            // Re-throw the error so the TalkToSalesModal can handle it
            // The modal will show the error state and allow the user to retry
            throw error;
        }
    };

    // Helper function to check if a product is already in comparison
    const isProductCompared = (productName: string) => {
        const productId = productName.toLowerCase().replace(/\s+/g, '-');
        return comparedProducts.some(p => p.id === productId);
    };

    const handleMiniCardCompareChange = (recommendation: ProductRecommendation, checked: boolean) => {
        const productId = recommendation.productName.toLowerCase().replace(/\s+/g, '-');
        
        if (checked) {
            // Add the recommendation product to comparison
            const comparisonProduct: ComparisonProduct = {
                id: productId,
                productName: recommendation.productName,
                description: recommendation.description,
                imageUrl: recommendation.imageUrl,
                matchPercentage: recommendation.matchPercentage,
                tags: recommendation.tags,
                features: recommendation.features,
                isTopRecommendation: recommendation.isTopRecommendation,
                model: recommendation.model // Pass the model field for API calls
            };
            
            setComparedProducts(prev => 
                prev.find(p => p.id === comparisonProduct.id) 
                    ? prev 
                    : [...prev, comparisonProduct]
            );
        } else {
            // Remove the product from comparison
            setComparedProducts(prev => 
                prev.filter(p => p.id !== productId)
            );
        }
    };

  const handleCompareChange = (checked: boolean) => {
    if (!chatState.topRecommendation) return;

    if (checked) {
      // Add the recommendation product to comparison
      const comparisonProduct: ComparisonProduct = {
        id: chatState.topRecommendation.productName
          .toLowerCase()
          .replace(/\s+/g, "-"),
        productName: chatState.topRecommendation.productName,
        description: chatState.topRecommendation.description,
        imageUrl: chatState.topRecommendation.imageUrl,
        matchPercentage: chatState.topRecommendation.matchPercentage,
        tags: chatState.topRecommendation.tags,
        features: chatState.topRecommendation.features,
        isTopRecommendation: chatState.topRecommendation.isTopRecommendation,
                model: chatState.topRecommendation.model // Pass the model field for API calls
      };

      setComparedProducts((prev) =>
        prev.find((p) => p.id === comparisonProduct.id)
          ? prev
          : [...prev, comparisonProduct]
      );
    } else {
      // Remove the product from comparison
      const productId = chatState.topRecommendation.productName
        .toLowerCase()
        .replace(/\s+/g, "-");
      setComparedProducts((prev) => prev.filter((p) => p.id !== productId));
    }
  };

  const handleShare = async () => {
    if (!chatState.topRecommendation) return;

    await shareProduct(
      chatState.topRecommendation.productName,
      chatState.topRecommendation.productUrl,
      (url) => {
        console.log("Product link copied to clipboard:", url);
        // TODO: Show success toast notification
      },
      (error) => {
        console.error("Failed to share product:", error);
        // TODO: Show error toast notification
      }
    );
  };

  // Comparison handlers
  const handleShowComparison = useCallback(() => {
    setIsComparisonSlideoutOpen(true);
  }, []);

  const handleDownloadPack = useCallback(() => {
    // Placeholder until download payload is defined.
  }, []);

  const handleCloseComparison = () => {
    setIsComparisonSlideoutOpen(false);
  };

  const handleRemoveFromComparison = (productId: string) => {
    setComparedProducts((prev) => prev.filter((p) => p.id !== productId));
  };

    const handleViewProductDetails = (productId: string) => {
        // Find the product in compared products by ID
        const comparedProduct = comparedProducts.find(p => p.id === productId);
        if (!comparedProduct) {
            console.warn('Product not found in compared products:', productId);
            return;
        }

        analyticsService.trackEvent('view_product', {
            product_name: comparedProduct.productName,
            product_sku: comparedProduct.model,
            source: 'comparison_slideout',
        });

        // Convert ComparisonProduct to ProductRecommendation format for the slideout
        const productRecommendation: ProductRecommendation = {
            productName: comparedProduct.productName || 'Unknown Product',
            description: comparedProduct.description || 'Product description not available',
            imageUrl: comparedProduct.imageUrl || '/av-logo.png',
            productUrl: undefined, // ComparisonProduct doesn't have productUrl
            tags: comparedProduct.tags || [],
            matchPercentage: comparedProduct.matchPercentage || 95,
            featureDescription: comparedProduct.features?.length 
                ? comparedProduct.features.join('. ') 
                : 'Advanced features for your space.',
            features: comparedProduct.features || [],
            isTopRecommendation: comparedProduct.isTopRecommendation || false,
            model: comparedProduct.model
        };

        // Open the same product details slideout used for mini cards
        setSelectedMiniCardDetails(productRecommendation);
        setIsMiniCardDetailsOpen(true);
    };

  const handleShareComparison = () => {
    // This would handle sharing the comparison
  };

  // Function to get initial recommendations after wizard completion
  const getInitialRecommendations = async () => {
    try {
      // Show loading
      addMessages([createLoadingMessage()]);
      setLoadingState(true);

      // Create a summary message to send to the backend
      const summaryMessage = `Based on my requirements: ${wizardAnswers.join(', ')}. Please recommend the best AV solutions.`;

      const response = await chatService.sendMessage(
        summaryMessage,
        convertMessagesToApiFormat(chatState.messages),
        "gpt-4.1"
      );

      // Update with response
      const assistantMessage = transformBackendResponseToUIMessage(response, getCurrentTimeString());
      replaceLastMessage(assistantMessage);
      updateRecommendationsIfAvailable(response, response.options ? transformOptionsToUIOptions(response.options) : []);

    } catch (error) {
      console.error("Error getting initial recommendations:", error);
      const errorMessage = {
        type: "assistant" as const,
        content: "Sorry, I encountered an error while getting your recommendations. Please try asking me directly about your needs.",
        author: "Solution assistant",
        timestamp: getCurrentTimeString(),
      };
      replaceLastMessage(errorMessage);
      setLoadingState(false);
    }
  };

  // Wizard handlers
  const handleWizardComplete = (answers: string[]) => {
    setWizardAnswers(answers);
    setShowWizard(false);
  };

  const handleResetToQuizStart = useCallback(() => {
    setShowWizard(true);
    setWizardAnswers([]);
    setChatState(createInitialChatStateValue());
    setComparedProducts([]);
    setIsComparisonSlideoutOpen(false);
    setSelectedMiniCardDetails(null);
    setIsMiniCardDetailsOpen(false);
    setIsContactSalesModalOpen(false);
    setHasInteractedWithChat(false);
    setLastSeenMobileMessageCount(0);
    setDrawerSnap("collapsed");
    setDrawerHeightPx(snapHeights.collapsed);
    setIsDrawerDragging(false);
    setKeyboardHeight(0);
    setKeyboardOpen(false);
  }, [snapHeights.collapsed]);

  useEffect(() => {
    setHeaderActions({
      compareCount: comparedProducts.length,
      onCompare: handleShowComparison,
      onDownload: handleDownloadPack,
      onContactSales: handleContactSales,
      onLogoClick: handleResetToQuizStart,
    });

    return () => setHeaderActions(DEFAULT_HEADER_ACTIONS);
  }, [
    comparedProducts.length,
    handleContactSales,
    handleDownloadPack,
    handleShowComparison,
    handleResetToQuizStart,
    setHeaderActions,
  ]);

  const chatInputPlaceholder = chatState.quizState.isActive
    ? "Ask a question or select an option above..."
    : "I'm looking for...";
  const showMobileDrawer =
    isMobileViewport && (hasInteractedWithChat || chatState.messages.length > 0);
  const showFloatingMobileInput = isMobileViewport && !showMobileDrawer;
  const isMobileDrawerCollapsed = drawerHeightPx <= snapHeights.collapsed + 8;
  const safeLastSeenCount = Math.min(lastSeenMobileMessageCount, chatState.messages.length);
  const unreadMobileAssistantMessageCount =
    showMobileDrawer && isMobileDrawerCollapsed
      ? chatState.messages
          .slice(safeLastSeenCount)
          .filter(
            (message) => message.type === "assistant" || message.type === "question"
          ).length
      : 0;
  const showMobileUnreadHint = unreadMobileAssistantMessageCount > 0;
  const recommendationsScrollStyle: CSSProperties | undefined = isMobileViewport
    ? {
        paddingBottom: showMobileDrawer
          ? `calc(${snapHeights.collapsed}px + env(safe-area-inset-bottom, 0px) + 16px)`
          : "104px",
      }
    : undefined;
  const mobileDrawerStyle = {
    "--drawer-height": `${drawerHeightPx}px`,
    "--keyboard-offset": `${keyboardHeight}px`,
  } as CSSProperties;

  return (
    <div className="app">
      <div className={`app-container ${isDesktopChatOpen ? "app-container--chat-open" : ""}`}>
        <section className="recommendations-section">
          <div className="recommendations-scrollable-content" style={recommendationsScrollStyle}>
            <div className="recommendations-content">
              {chatState.topRecommendation ? (
                <HeroRecommendationCard
                  productName={chatState.topRecommendation.productName}
                  description={chatState.topRecommendation.description}
                  imageUrl={chatState.topRecommendation.imageUrl}
                  tags={chatState.topRecommendation.tags}
                  matchPercentage={chatState.topRecommendation.matchPercentage}
                  featureDescription={
                    chatState.topRecommendation.featureDescription
                  }
                  isTopRecommendation={
                    chatState.topRecommendation.isTopRecommendation
                  }
                  isCompared={comparedProducts.some(
                    (p) =>
                      p.id ===
                      chatState.topRecommendation?.productName
                        .toLowerCase()
                        .replace(/\s+/g, "-")
                  )}
                  onViewDetails={handleViewDetails}
                  onCompareChange={handleCompareChange}
                  onShare={handleShare}
                />
              ) : chatState.isLoading ? (
                <ShimmerCard />
              ) : (
                <div className="no-recommendations">
                  <p>
                    Start chatting to get personalized product recommendations!
                  </p>
                </div>
              )}
            </div>

            {(chatState.additionalRecommendations.length > 0 ||
              chatState.isLoading) && (
              <div className="you-might-like">
                <h3>YOU MIGHT ALSO LIKE</h3>
                <div className="additional-recommendations">
                  {chatState.additionalRecommendations.length > 0
                    ? chatState.additionalRecommendations.map(
                        (recommendation, index) => (
                          <div key={index} className="mini-recommendation-card">
                            {recommendation.imageUrl && (
                              <div className="mini-card-image">
                                <img
                                  src={recommendation.imageUrl}
                                  alt={recommendation.productName}
                                  className="mini-card-img"
                                />
                              </div>
                            )}
                            <div className="mini-card-content">
                              <h4 className="mini-card-title">
                                {recommendation.productName}
                              </h4>
                              <p className="mini-card-description">
                                {recommendation.description}
                              </p>
                              {recommendation.matchPercentage && (
                                <div className="mini-card-match">
                                  {recommendation.matchPercentage}% match
                                </div>
                              )}
                              <div className="mini-card-actions">
                                <Button
                                  type="primary"
                                  size="small"
                                  icon={<EyeIcon />}
                                  iconPosition="left"
                                  onClick={() =>
                                    handleMiniCardViewDetails(recommendation)
                                  }
                                >
                                  VIEW DETAILS
                                </Button>
                                <button
                                  className={`mini-card-compare-button ${isProductCompared(recommendation.productName) ? 'mini-card-compare-button--active' : ''}`}
                                  onClick={() => handleMiniCardCompareChange(recommendation, !isProductCompared(recommendation.productName))}
                                  title={isProductCompared(recommendation.productName) ? 'Remove from comparison' : 'Add to comparison'}
                                >
                                  <CompareIcon />
                                  {isProductCompared(recommendation.productName) ? 'Added' : 'Compare'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      )
                    : chatState.isLoading && (
                        <>
                          <MiniShimmerCard />
                          <MiniShimmerCard />
                          <MiniShimmerCard />
                        </>
                      )}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      <aside className={`desktop-chat-panel ${isDesktopChatOpen ? "is-open" : "is-closed"}`}>
        <div className="chat-messages" ref={desktopChatMessagesRef}>
          {chatState.messages.map((message, index) => (
            <ChatMessage
              key={`desktop-${index}`}
              type={message.type}
              content={message.content}
              questionNumber={message.questionNumber}
              totalQuestions={message.totalQuestions}
              timestamp={message.timestamp}
              author={message.author}
            />
          ))}
        </div>

        {chatState.currentOptions.length > 0 && (
          <div className="chat-options">
            <ChatOptionList
              options={chatState.currentOptions}
              onOptionSelect={handleOptionSelect}
              allowMultipleSelection={false}
            />
          </div>
        )}

        <div className="chat-input-section">
          <ChatInput
            onSend={handleSendMessage}
            placeholder={chatInputPlaceholder}
            disabled={chatState.isLoading}
          />
        </div>
      </aside>

      <button
        type="button"
        className={`desktop-chat-edge-toggle ${isDesktopChatOpen ? "is-open" : "is-closed"}`}
        onClick={() => setIsDesktopChatOpen((prev) => !prev)}
        aria-label={isDesktopChatOpen ? "Hide assistant" : "Show assistant"}
      >
        <span className={`desktop-chat-edge-toggle__chevron ${isDesktopChatOpen ? "is-open" : ""}`}>
          ❮
        </span>
      </button>

      {showFloatingMobileInput && (
        <div className="mobile-floating-input">
          <ChatInput
            onSend={handleSendMessage}
            placeholder={chatInputPlaceholder}
            disableSend={chatState.isLoading}
            disableInput={false}
          />
        </div>
      )}

      {showMobileDrawer && (
        <section
          className={`mobile-chat-drawer ${isMobileDrawerCollapsed ? "is-collapsed" : ""} ${
            keyboardOpen ? "keyboard-open" : ""
          } ${isDrawerDragging ? "is-dragging" : ""}`}
          style={mobileDrawerStyle}
        >
          <button
            type="button"
            className={`mobile-chat-drawer-handle ${showMobileUnreadHint ? "has-unread" : ""}`}
            aria-label={
              showMobileUnreadHint
                ? `${unreadMobileAssistantMessageCount} new message${
                    unreadMobileAssistantMessageCount === 1 ? "" : "s"
                  } in chat drawer`
                : "Resize chat drawer"
            }
            onPointerDown={handleDrawerPointerDown}
            onPointerMove={handleDrawerPointerMove}
            onPointerUp={handleDrawerPointerUp}
            onPointerCancel={handleDrawerPointerCancel}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              cycleDrawerSnap();
            }}
          >
            <span className="mobile-chat-drawer-handle-bar" />
            {showMobileUnreadHint && (
              <span className="mobile-chat-drawer-unread-hint" aria-live="polite">
                {unreadMobileAssistantMessageCount === 1
                  ? "New message"
                  : `${unreadMobileAssistantMessageCount} new messages`}
              </span>
            )}
            {!isMobileDrawerCollapsed && (
              <span className="mobile-chat-drawer-handle-label">Drag to resize</span>
            )}
          </button>

          <div className={`mobile-chat-messages-wrap ${isMobileDrawerCollapsed ? "is-hidden" : ""}`}>
            <div className="mobile-chat-messages" ref={mobileChatMessagesRef}>
              {chatState.messages.map((message, index) => (
                <ChatMessage
                  key={`mobile-${index}`}
                  type={message.type}
                  content={message.content}
                  questionNumber={message.questionNumber}
                  totalQuestions={message.totalQuestions}
                  timestamp={message.timestamp}
                  author={message.author}
                />
              ))}
            </div>

            {chatState.currentOptions.length > 0 && (
              <div className="chat-options mobile-chat-options">
                <ChatOptionList
                  options={chatState.currentOptions}
                  onOptionSelect={handleOptionSelect}
                  allowMultipleSelection={false}
                />
              </div>
            )}
          </div>

          <div className={`chat-input-section mobile-chat-input ${isMobileDrawerCollapsed ? "is-collapsed" : ""}`}>
            <ChatInput
              onSend={handleSendMessage}
              placeholder={chatInputPlaceholder}
              disableSend={chatState.isLoading}
              disableInput={false}
            />
          </div>
        </section>
      )}

      {/* Comparison Slideout */}
      <ComparisonSlideout
        isOpen={isComparisonSlideoutOpen}
        onClose={handleCloseComparison}
        products={comparedProducts}
        onRemoveProduct={handleRemoveFromComparison}
        onViewDetails={handleViewProductDetails}
        onShare={handleShareComparison}
      />

      {/* Mini Card Product Details Slideout */}
      {selectedMiniCardDetails && (
        <ProductDetailsSlideout
          isOpen={isMiniCardDetailsOpen}
          onClose={handleCloseMiniCardDetails}
          productName={selectedMiniCardDetails.productName}
          description={selectedMiniCardDetails.description}
          heroImageUrl={selectedMiniCardDetails.imageUrl}
          imageAlt={selectedMiniCardDetails.productName}
          matchPercentage={selectedMiniCardDetails.matchPercentage}
          matchDescription={selectedMiniCardDetails.featureDescription}
          isTopRecommendation={selectedMiniCardDetails.isTopRecommendation}
          overview={`The ${
            selectedMiniCardDetails.productName
          } provides a complete video conference room solution for open-platform UC applications. ${
            selectedMiniCardDetails.featureDescription || ""
          }`}
          onTalkToSales={() =>
            console.log(
              "Talk to Sales clicked for:",
              selectedMiniCardDetails.productName
            )
          }
          onAskAI={() =>
            console.log(
              "Ask AI clicked for:",
              selectedMiniCardDetails.productName
            )
          }
          onShare={() =>
            console.log(
              "Share clicked for:",
              selectedMiniCardDetails.productName
            )
          }
        />
      )}


      {/* Contact Sales Modal */}
      <TalkToSalesModal
        isOpen={isContactSalesModalOpen}
        onClose={() => setIsContactSalesModalOpen(false)}
        onSubmit={handleContactSalesSubmit}
      />

      {/* Question Wizard Overlay */}
      {showWizard && (
        <QuestionWizard
          onComplete={handleWizardComplete}
        />
      )}
    </div>
  );
}

export default App;
